// When the ONE one-time Add-to-Home-Screen card shows (IMPLEMENTATION §2.6 / L1 "one-time A2HS note",
// §3 M11 "iOS A2HS hint", §6.3 "Add to Home Screen first"; UX §44, GDD §113). One card, one flag
// (`meta.uiFlags.a2hs`, written when it shows), never inside a Home Screen app (standalone):
//   'ios'   iOS Safari: at once when `Layout.note` (cells < 48 px), else after `ui.json lifecycle.a2hsAfterSec`
//           (120) of VISIBLE play in this session — the how-to (Share → Add to Home Screen) with the note that
//           the Home Screen app starts its own save (copy.json overlay.a2hsHint)
//   'note'  every other browser: only when `Layout.note` — the layout note (copy.json overlay.a2hsNote)
// Re-checked on window resize (a relayout). Off in ?test runs unless ?a2hs=1 (env.a2hs). Pure scheduling:
// the card itself is ui/overlays (A2hsHint.tsx).

export type A2hsKind = 'ios' | 'note';

export interface A2hsInput {
  enabled: boolean;
  iosSafari: boolean;
  standalone: boolean;
  /** meta.uiFlags.a2hs already set (shown in an earlier session). */
  flagged: boolean;
  /** Layout.note: cells below 48 px. */
  note: boolean;
  visibleMs: number;
  afterMs: number;
}

/** Which card is due now (null: none). */
export function a2hsDue(i: A2hsInput): A2hsKind | null {
  if (!i.enabled || i.standalone || i.flagged) return null;
  if (i.iosSafari) return i.note || i.visibleMs >= i.afterMs ? 'ios' : null;
  return i.note ? 'note' : null;
}

export interface A2hsWatchDeps {
  win: Window;
  enabled: boolean;
  iosSafari: boolean;
  standalone: boolean;
  afterMs: number;
  flagged(): boolean;
  note(): boolean;
  /** Monotonic ms (performance.now). */
  now(): number;
  /** Shows the card (and sets the flag); called at most once. */
  show(kind: A2hsKind): void;
}

export interface A2hsWatch {
  /** Re-evaluates now (boot, relayout). */
  check(): void;
  visibleMs(): number;
  shown(): boolean;
  dispose(): void;
}

/** Counts visible time and shows the card once it is due; inert when it can never be due. */
export function watchA2hs(d: A2hsWatchDeps): A2hsWatch {
  const doc = d.win.document;
  let acc = 0;
  let since: number | null = doc.visibilityState === 'visible' ? d.now() : null;
  let timer: number | null = null;
  let done = false;
  let showed = false;
  const possible = d.enabled && !d.standalone;

  const visibleMs = (): number => acc + (since === null ? 0 : d.now() - since);
  const clear = (): void => {
    if (timer !== null) d.win.clearTimeout(timer);
    timer = null;
  };
  const stop = (): void => {
    clear();
    doc.removeEventListener('visibilitychange', onVis);
    d.win.removeEventListener('resize', check);
  };
  function check(): void {
    if (done || !possible) return;
    const due = a2hsDue({
      enabled: d.enabled,
      iosSafari: d.iosSafari,
      standalone: d.standalone,
      flagged: d.flagged(),
      note: d.note(),
      visibleMs: visibleMs(),
      afterMs: d.afterMs,
    });
    if (due !== null) {
      done = true;
      showed = true;
      stop();
      d.show(due);
      return;
    }
    if (d.flagged()) {
      done = true;
      stop();
      return;
    }
    clear();
    // only iOS Safari counts visible time; elsewhere a relayout (resize) is the only trigger
    if (d.iosSafari && since !== null)
      timer = d.win.setTimeout(check, Math.max(0, d.afterMs - visibleMs()));
  }
  function onVis(): void {
    if (doc.visibilityState === 'visible') {
      if (since === null) since = d.now();
    } else if (since !== null) {
      acc += d.now() - since;
      since = null;
    }
    check();
  }
  if (possible) {
    doc.addEventListener('visibilitychange', onVis);
    d.win.addEventListener('resize', check);
  }
  return {
    check,
    visibleMs,
    shown: () => showed,
    dispose: stop,
  };
}
